import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useMaybeFetchServiceFee } from '../../../hooks/useMaybeFetchServiceFee';
import { useSearchParams } from '../../../hooks/useSearchParams';
import selectors from '../../../store/selectors';
import { mobileOrHomePhoneRegexPattern, nameRegexPattern } from '../../../utils/string';
import FormInputError from '../../Form/FormInputError';
import OfferingAmount from '../../Form/OfferingAmount';
import SubmitAddMassOffering from '../../Form/rhf/SubmitAddMassOffering/SubmitAddMassOffering';
import ServiceFee from '../../ServiceFee/ServiceFee';
import VariousMassOfferingSelector, {
  OFFERING_TYPE_WITH_TIME,
} from '../../VariousMassOfferingSelector/VariousMassOfferingSelector';
import { useCrossLinkFromInbox } from '../hooks/useCrossLinkFromInbox';

import { inboxMassOfferingsFormNames } from './inboxMassOfferingsFormNames';

const accountIdsWithPredefinedHidden = [
  // '654463132773478400', // demo-deum
  // '1516868980247126016', // deum-demo
  '1592170051063463936', // WSDSAC - Ołtarzew
];

const useDefaultValues = ({
  isServiceFeeEnabled,
  isServiceFeeSelectedCoveredDefault,
  isServiceFeeCoveredRequired,
  inboxDictionary,
}) => {
  const urlSearchParams = useSearchParams();

  return useMemo(() => {
    const typeFromUrl = urlSearchParams.get('type');
    const predefinedType =
      (typeFromUrl && inboxDictionary.find(item => item.title === typeFromUrl)?.id) ??
      inboxDictionary[0].id;

    return {
      [inboxMassOfferingsFormNames.agreeOnRegulations]: false,
      [inboxMassOfferingsFormNames.email]: '',
      [inboxMassOfferingsFormNames.fullName]: '',
      [inboxMassOfferingsFormNames.offeringAmount]: null,
      [inboxMassOfferingsFormNames.totalAmountToPay]: null,
      [inboxMassOfferingsFormNames.offeringContent]: '',
      [inboxMassOfferingsFormNames.offeringType]: predefinedType,
      [inboxMassOfferingsFormNames.phone]: '',
      [inboxMassOfferingsFormNames.userCoversServiceFee]:
        isServiceFeeEnabled && (isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault),

      // [inboxMassOfferingsFormNames.showAddress]: false,
      // [inboxMassOfferingsFormNames.country]: 'Polska',
      // [inboxMassOfferingsFormNames.city]: '',
      // [inboxMassOfferingsFormNames.zipCode]: '',
      // [inboxMassOfferingsFormNames.street]: '',
    };
  }, []);
};

export const InboxMassOfferingsForm = ({ onSubmit, loading, inboxDictionary }) => {
  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);
  const isServiceFeeSelectedCoveredDefault = useSelector(
    selectors.selectIsServiceFeeCoveredDefault,
  );
  const isServiceFeeCoveredRequired = useSelector(selectors.selectIsServiceFeeCoveredRequired);

  const defaultValues = useDefaultValues({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault,
    isServiceFeeCoveredRequired,
    inboxDictionary,
  });

  const intencjeOnlineUrl = useCrossLinkFromInbox();
  const accountId = useSelector(selectors.getAccountId);

  const beforeFormText = useSelector(selectors.selectPerpetualMassBeforeFormText);

  const massOfferingAmount = useSelector(selectors.selectMassOfferingAmountOption);

  const regulationsUrl = useSelector(selectors.selectRegulationsUrl);

  const formSchema = useMemo(() => {
    const inboxDictionaryValues = inboxDictionary.map(item => item.id);

    return yup.object().shape({
      [inboxMassOfferingsFormNames.agreeOnRegulations]: yup
        .bool()
        .oneOf([true], 'Musisz zgodzić się na regulamin, żeby zamówić intencję'),
      [inboxMassOfferingsFormNames.email]: yup.string().email().required('To pole jest wymagane'),
      [inboxMassOfferingsFormNames.fullName]: yup
        .string()
        .matches(new RegExp(nameRegexPattern, 'u'), 'Podaj poprawne imię i nazwisko'),
      [inboxMassOfferingsFormNames.offeringAmount]: yup
        .number()
        .min(massOfferingAmount.minimalValue, 'Podaj poprawną kwotę')
        .required('To pole jest wymagane')
        .typeError('Podaj poprawną kwotę (tylko cyfry)'),
      [inboxMassOfferingsFormNames.offeringContent]: yup
        .string()
        .required('Uzupełnij treść intencji')
        .min(3, 'Uzupełnij treść intencji'),
      [inboxMassOfferingsFormNames.offeringType]: yup
        .string()
        .oneOf(inboxDictionaryValues)
        .required('To pole jest wymagane'),
      [inboxMassOfferingsFormNames.phone]: yup.string().matches(
        // /(^((\+48)|(0048))?[ -]?[0-9]{3}[ -]?[0-9]{3}[ -]?[0-9]{3}$)|(^[0-9]{2}?[ -]?[0-9]{3}[ -]?[0-9]{2}[ -]?[0-9]{2}$)/
        new RegExp(mobileOrHomePhoneRegexPattern),
        {
          excludeEmptyString: true,
          message:
            'Podaj poprawny numer telefonu, np. w formacie 123 456 789 lub 22 123 12 12, lub +48 123 456 789',
        },
      ),
      // [inboxMassOfferingsFormNames.showAddress]: yup.bool(),
      // [inboxMassOfferingsFormNames.country]: yup.string().when(inboxMassOfferingsFormNames.showAddress, {
      //   is: true,
      //   then: yup.string().required('To pole jest wymagane'),
      // }),
      // [inboxMassOfferingsFormNames.city]: yup.string().when(inboxMassOfferingsFormNames.showAddress, {
      //   is: true,
      //   then: yup.string().required('To pole jest wymagane'),
      // }),
      // [inboxMassOfferingsFormNames.zipCode]: yup
      //   .string()
      //   .matches(new RegExp(`^[0-9]{2}-[0-9]{3}$`), {
      //     excludeEmptyString: true,
      //     message: 'Format: 00-000',
      //   })
      //   .when(inboxMassOfferingsFormNames.showAddress, {
      //     is: true,
      //     then: yup.string().required('To pole jest wymagane'),
      //   }),
      // [inboxMassOfferingsFormNames.street]: yup.string().when(inboxMassOfferingsFormNames.showAddress, {
      //   is: true,
      //   then: yup.string().required('To pole jest wymagane'),
      // }),
    });
  }, [massOfferingAmount.minimalValue, inboxDictionary]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    setError,
    clearErrors,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const totalAmountToPay = watch(inboxMassOfferingsFormNames.totalAmountToPay);
  const offeringAmount = watch(inboxMassOfferingsFormNames.offeringAmount);

  const { loading: serviceFeeLoading } = useMaybeFetchServiceFee({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault:
      isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault,
    offeringAmount,
    setTotalAmountToPay: value => setValue(inboxMassOfferingsFormNames.totalAmountToPay, value),
    userCoversServiceFee: watch(inboxMassOfferingsFormNames.userCoversServiceFee),
  });

  const hidePredefinedAmounts = accountIdsWithPredefinedHidden.includes(accountId);

  return (
    <div>
      <h2 className="el-h4 mb-4">Intencje online</h2>

      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={{ __html: beforeFormText }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Space direction="vertical" className="d-flex mt-4" size="large">
          <div>
            <Controller
              control={control}
              name={inboxMassOfferingsFormNames.offeringType}
              render={({ field }) => (
                <VariousMassOfferingSelector
                  disabled={loading}
                  fieldName={field.name}
                  fieldValue={field.value}
                  inboxDictionary={inboxDictionary}
                  onChange={newType => {
                    if (newType === OFFERING_TYPE_WITH_TIME.value) {
                      // eslint-disable-next-line no-restricted-globals
                      location.href = intencjeOnlineUrl;
                    }

                    field.onChange(newType);
                  }}
                />
              )}
            />
          </div>

          <div>
            <Typography.Title level={4}>Treść intencji</Typography.Title>
            <Typography.Text>
              Możemy kontaktować się z Tobą, jeśli intencja modlitwy nie będzie dla nas jasna.
            </Typography.Text>
            <Controller
              control={control}
              name={inboxMassOfferingsFormNames.offeringContent}
              render={({ field }) => {
                return (
                  <Input.TextArea
                    placeholder="Np. O potrzebne łaski dla ..., o zdrowie dla ..."
                    rows={3}
                    showCount
                    maxLength={300}
                    allowClear
                    disabled={loading}
                    name={field.name}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    value={field.value}
                  />
                );
              }}
            />
            {errors[inboxMassOfferingsFormNames.offeringContent] && (
              <FormInputError
                errorText={errors[inboxMassOfferingsFormNames.offeringContent].message}
              />
            )}
          </div>

          <div>
            <Controller
              control={control}
              name={inboxMassOfferingsFormNames.offeringAmount}
              render={({ field }) => {
                return (
                  <OfferingAmount
                    allowFreeOfferings={false}
                    clearErrors={clearErrors}
                    hidePredefinedAmounts={hidePredefinedAmounts}
                    loading={loading}
                    massOfferingAmount={massOfferingAmount}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    setError={setError}
                    value={field.value}
                    showLabel
                  />
                );
              }}
            />
            {errors[inboxMassOfferingsFormNames.offeringAmount] && (
              <FormInputError
                errorText={errors[inboxMassOfferingsFormNames.offeringAmount].message}
              />
            )}
          </div>

          {isServiceFeeEnabled && (
            <ServiceFee
              inputFieldName={inboxMassOfferingsFormNames.userCoversServiceFee}
              isRequired={isServiceFeeCoveredRequired}
              offeringAmount={offeringAmount}
              register={register}
              serviceFeeLoading={serviceFeeLoading}
              totalAmountToPay={totalAmountToPay}
            />
          )}

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              autoComplete="name"
              type="text"
              id={inboxMassOfferingsFormNames.fullName}
              {...register(inboxMassOfferingsFormNames.fullName)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={inboxMassOfferingsFormNames.fullName}>
              Imię i nazwisko zamawiającego
            </label>

            {errors[inboxMassOfferingsFormNames.fullName] && (
              <FormInputError errorText={errors[inboxMassOfferingsFormNames.fullName].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              id={inboxMassOfferingsFormNames.email}
              type="email"
              autoComplete="email"
              {...register(inboxMassOfferingsFormNames.email)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={inboxMassOfferingsFormNames.email}>E-mail zamawiającego</label>

            {errors[inboxMassOfferingsFormNames.email] && (
              <FormInputError errorText={errors[inboxMassOfferingsFormNames.email].message} />
            )}
          </div>

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-control"
              type="tel"
              autoComplete="tel"
              id={inboxMassOfferingsFormNames.phone}
              {...register(inboxMassOfferingsFormNames.phone)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={inboxMassOfferingsFormNames.phone}>Numer telefonu zamawiającego</label>

            {errors[inboxMassOfferingsFormNames.phone] && (
              <FormInputError errorText={errors[inboxMassOfferingsFormNames.phone].message} />
            )}
          </div>

          {/* <div> */}
          {/*  <div className="md-form input-group mt-0 mb-0"> */}
          {/*    <input */}
          {/*      disabled={loading} */}
          {/*      className="form-check-input" */}
          {/*      type="checkbox" */}
          {/*      // autoComplete="tel" */}
          {/*      id={inboxMassOfferingsFormNames.showAddress} */}
          {/*      {...register(inboxMassOfferingsFormNames.showAddress)} */}
          {/*    /> */}
          {/*    /!* eslint-disable-next-line jsx-a11y/label-has-associated-control *!/ */}
          {/*    <label htmlFor={inboxMassOfferingsFormNames.showAddress}> */}
          {/*      Chcę otrzymać papierowe potwierdzenie zamówienia intencji */}
          {/*    </label> */}
          {/*  </div> */}

          {/* </div> */}

          <div className="md-form input-group mt-0 mb-0">
            <input
              disabled={loading}
              className="form-check-input"
              type="checkbox"
              value="1"
              id={inboxMassOfferingsFormNames.agreeOnRegulations}
              {...register(inboxMassOfferingsFormNames.agreeOnRegulations)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              className="form-check-label w-100"
              htmlFor={inboxMassOfferingsFormNames.agreeOnRegulations}
              id={`label-${inboxMassOfferingsFormNames.agreeOnRegulations}`}
            >
              Akceptuję{' '}
              <a target="_blank" rel="noreferrer" href={regulationsUrl}>
                Regulamin
              </a>
            </label>

            {errors[inboxMassOfferingsFormNames.agreeOnRegulations] && (
              <FormInputError
                className="mt-2"
                errorText={errors[inboxMassOfferingsFormNames.agreeOnRegulations].message}
              />
            )}
          </div>
        </Space>

        <SubmitAddMassOffering
          name={inboxMassOfferingsFormNames.offeringAmount}
          disabled={!isValid}
          watch={watch}
          loading={loading}
          isOnlinePayment
          isPaymentExpiring
        />
      </form>
    </div>
  );
};

InboxMassOfferingsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  inboxDictionary: PropTypes.array.isRequired,
};
